/**
 * Blog API client.
 *
 * Read + export only. Writes go through the chat (orchestrator spawns
 * blog-writer agent → MCP save_blog_post).
 */

import api from './api';
import type {
  BlogDoc,
  BlogMeta,
  BlogRenderTarget,
} from '@agentic-ivy/shared-types';

export interface GetBlogResponse {
  exists: boolean;
  doc: BlogDoc | null;
  meta: BlogMeta | null;
  videoManifestReady: boolean;
  /** True when a BlogPost row exists with status=DRAFTING and no doc has been saved yet. */
  generating: boolean;
  generationStatus: 'DRAFTING' | 'READY' | 'EDITING' | 'FAILED' | null;
}

export interface ExportBlogResponse {
  target: BlogRenderTarget;
  content: string;
  mimeType: string;
  cached: boolean;
}

export interface MarkGeneratingResponse {
  blogPostId: string;
  status: 'DRAFTING' | 'READY' | 'EDITING' | 'FAILED';
  version: number;
  startedAt: string;
}

export interface BlogPublishResponse {
  shareToken: string;
  shareUrl: string;
  isActive: boolean;
  createdAt: string;
  ogImageUrl: string | null;
}

export type BlogPublishStatusResponse =
  | { published: false }
  | {
      published: true;
      shareToken: string;
      shareUrl: string;
      createdAt: string;
      viewCount: number;
    };

export const blogAPI = {
  get: async (conversationId: string): Promise<GetBlogResponse> => {
    const response = await api.get(`/conversations/${conversationId}/blog`);
    return response.data;
  },

  markGenerating: async (conversationId: string): Promise<MarkGeneratingResponse> => {
    const response = await api.post(
      `/conversations/${conversationId}/blog/mark-generating`,
    );
    return response.data;
  },

  export: async (
    conversationId: string,
    target: BlogRenderTarget,
  ): Promise<ExportBlogResponse> => {
    const response = await api.post(`/conversations/${conversationId}/blog/export`, {
      target,
    });
    return response.data;
  },

  publish: async (conversationId: string): Promise<BlogPublishResponse> => {
    const response = await api.post(`/conversations/${conversationId}/blog/publish`);
    return response.data;
  },

  unpublish: async (conversationId: string): Promise<{ unpublished: true }> => {
    const response = await api.delete(`/conversations/${conversationId}/blog/publish`);
    return response.data;
  },

  getPublishStatus: async (
    conversationId: string,
  ): Promise<BlogPublishStatusResponse> => {
    const response = await api.get(`/conversations/${conversationId}/blog/publish-status`);
    return response.data;
  },
};
